import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import apiClient from '../api/api-client'
import { AxiosResponse } from 'axios'

import { AbschlussArt, Studiengang, StudiengangCollection } from '../model'
import { useLoading } from '@/js/utils/useLoading'
import { CreateStudiengangDto } from '@/js/types/CreateStudiengangDto'

export const useStudiengangStore = defineStore('studiengang', () => {
    const { isLoading, setLoading, errors, getError, hasError } = useLoading()

    const studiengaenge = ref<Array<Studiengang>>([])
    const availableStudienformen = ref<string[]>([])
    const availableAbschlussArten = ref<AbschlussArt[]>([])

    const getStudiengaenge = computed(() => studiengaenge)
    const getStudiengangById = computed(() => {
        return (studiengangId: string) => {
            return studiengaenge.value.find((studiengang) => {
                return studiengang.studiengangId === studiengangId
            })
        }
    })
    const getStudiengangByIri = computed(() => {
        return (studiengangIri: string) => {
            return studiengaenge.value.find((studiengang) => {
                return studiengang['@id'] === studiengangIri
            })
        }
    })

    const getStudiengangByKurzName = computed(() => {
        return (kurzName: string) => {
            return studiengaenge.value.find((studiengang) => {
                return studiengang.kurzName?.toLowerCase() === kurzName.toLowerCase()
            })
        }
    })

    const replaceStudiengangInState = (newStudiengang: Studiengang) => {
        const index = studiengaenge.value.findIndex(
            (studiengang) => studiengang.studiengangId === newStudiengang.studiengangId
        )
        studiengaenge.value.splice(index, 1, newStudiengang)
    }

    async function fetchStudiengaenge(): Promise<void> {
        await setLoading('fetching-studiengaenge', async () => {
            studiengaenge.value = []
            const url = '/api/studiengaenge'
            const response: AxiosResponse<StudiengangCollection, any> = await apiClient.get(url, {
                params: {
                    pagination: false
                }
            })
            studiengaenge.value = response.data['hydra:member']
        })
    }

    async function updateStudiengang(
        studiengangId: string,
        studiengang: Studiengang
    ): Promise<void> {
        await setLoading('save-studiengang', async () => {
            const response: AxiosResponse<Studiengang, any> = await apiClient.patch(
                `/api/studiengaenge/${studiengangId}`,
                {
                    studiengangId: studiengangId,
                    name: studiengang.name,
                    kurzName: studiengang.kurzName,
                    standort: studiengang.standort,
                    studienformen: studiengang.studienformen?.studienformen,
                    abschlussArt: studiengang.abschlussArt?.abschluss
                },
                { headers: { 'Content-Type': 'application/merge-patch+json' } }
            )
            replaceStudiengangInState(response.data)
        })
    }

    async function createStudiengang(createStudiengangDto: CreateStudiengangDto): Promise<void> {
        await setLoading('create-studiengang', async () => {
            const response: AxiosResponse<Studiengang, any> = await apiClient.post(
                `/api/studiengaenge/create`,
                {
                    fachbereich: createStudiengangDto.fachbereich,
                    name: createStudiengangDto.name,
                    kurzName: createStudiengangDto.kurzName,
                    standort: createStudiengangDto.standort,
                    studienformen: createStudiengangDto.studienformen,
                    abschlussArt: createStudiengangDto.abschlussArt
                },
                { headers: { 'Content-Type': 'application/ld+json' } }
            )
            replaceStudiengangInState(response.data)
        })
    }

    async function fetchAvailableStudienformen(): Promise<void> {
        if (availableStudienformen.value.length > 0) {
            return
        }

        const response = await apiClient.get('/api/available-studienformen')
        availableStudienformen.value = response.data['hydra:member']
    }

    async function fetchAvailableAbschlussArten(): Promise<void> {
        if (availableAbschlussArten.value.length > 0) {
            return
        }

        const response = await apiClient.get('/api/available-abschlussarten')
        availableAbschlussArten.value = response.data['hydra:member']
    }

    async function uploadStudiengangFile(file: string): Promise<boolean> {
        try {
            await setLoading('upload-studiengang', async () => {
                await apiClient.post('/api/studiengang/upload', { fileData: file })
                await fetchStudiengaenge()
            })
            return true
        } catch {
            return false
        }
    }

    return {
        studiengaenge,
        availableStudienformen,
        availableAbschlussArten,
        isLoading,
        errors,
        hasError,
        getError,
        fetchStudiengaenge,
        fetchAvailableStudienformen,
        fetchAvailableAbschlussArten,
        updateStudiengang,
        createStudiengang,
        uploadStudiengangFile,
        getStudiengaenge,
        getStudiengangById,
        getStudiengangByIri,
        getStudiengangByKurzName
    }
})
